/**
 * Subir el original de una factura escaneada y colgárselo a su fila.
 *
 * DIRECTO DEL NAVEGADOR AL BUCKET, sin función de servidor por medio. Es lo que
 * hace que esto funcione igual desde el móvil, la tableta, el navegador o la
 * vista previa de Lovable: es el mismo JavaScript en los cuatro sitios, y no hay
 * nada que pueda estar «sin desplegar» (que es exactamente lo que pasó con
 * `archive-invoice-file`, ver `invoiceFileOutbox`).
 *
 * De paso sube el archivo TAL CUAL, no en base64: la vuelta por una función de
 * servidor obligaba a convertirlo a texto y eso lo engordaba un tercio, en el
 * mismo sitio donde el tamaño ya era el problema.
 *
 * Nunca lanza. Devuelve si se guardó, y quien llama decide si vuelve a
 * intentarlo — que es lo que hace la cola.
 */
import { supabase } from "@/integrations/supabase/client";
import {
  dropInvoiceFileJob,
  listInvoiceFileJobs,
  markInvoiceFileAttempt,
  type InvoiceFileJob,
} from "@/lib/invoiceFileOutbox";

const BUCKET = "invoices";

/** La misma ruta que escribía el servidor: `<empresa>/<sha256>.<ext>`. */
export function invoiceStoragePath(companyId: string, fileHash: string, mime: string): string {
  const ext =
    mime.includes("pdf") ? "pdf"
    : mime.includes("png") ? "png"
    : mime.includes("webp") ? "webp"
    : mime.includes("heic") || mime.includes("heif") ? "heic"
    : mime.includes("jpeg") || mime.includes("jpg") ? "jpg"
    : "bin";
  return `${companyId}/${fileHash}.${ext}`;
}

/** Que ya esté subido es lo NORMAL al reintentar: no es un fallo. */
function alreadyThere(message: string | undefined): boolean {
  return !!message && /exist|duplicate|409|resource already/i.test(message);
}

type ArchiveOutcome =
  /** En el bucket y colgado de su fila. */
  | "stored"
  /** No se pudo ahora; los bytes siguen en la cola. NUNCA se tiran sin subir. */
  | "retry";

/**
 * Un intento.
 *
 * El id de la fila puede venir vacío —el escaneo se cortó antes de responder—,
 * y entonces se busca por `file_hash`, que es lo que el servidor guarda al
 * registrar la factura. Si tampoco aparece (la factura nunca llegó a
 * registrarse), el archivo se sube IGUAL a la carpeta de la empresa: nunca se
 * tira la única copia.
 */
export async function archiveInvoiceFileOnce(job: InvoiceFileJob): Promise<ArchiveOutcome> {
  let invoiceId = job.invoiceId;

  if (!invoiceId) {
    const { data, error } = await supabase
      .from("scanned_invoices")
      .select("id, storage_path")
      .eq("company_id", job.companyId)
      .eq("file_hash", job.fileHash)
      .maybeSingle();
    // Un error de red aquí no es «no existe»: se reintenta.
    if (error) return "retry";
    if (data?.storage_path) return "stored";
    invoiceId = data?.id ?? null;
  }

  const path = invoiceStoragePath(job.companyId, job.fileHash, job.mime);

  /* SIN FICHA, EL ARCHIVO SE GUARDA IGUAL (21-10-2026, Pablo: «siempre que se
     escanea una factura, se guarda el documento, da igual que esté repetida»).
     Antes, sin fila se «abandonaba» y la cola BORRABA la única copia: así se
     perdió la 2.ª factura de Hortalan de SAT Inver, que el escáner tomó por
     repetida. Ahora el original sube a la carpeta de la empresa con su huella;
     la ficha, si llega después, lo encuentra en la misma ruta. Solo se deja de
     intentar cuando está en el bucket. */
  if (!invoiceId) {
    const { error: upErr } = await supabase.storage
      .from(BUCKET)
      .upload(path, job.blob, { contentType: job.mime, upsert: false });
    if (upErr && !alreadyThere(upErr.message)) {
      console.warn("[archiveInvoiceFile] subida sin ficha rechazada:", upErr.message);
      return "retry";
    }
    return "stored";
  }

  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, job.blob, { contentType: job.mime, upsert: false });
  if (upErr && !alreadyThere(upErr.message)) {
    console.warn("[archiveInvoiceFile] subida rechazada:", upErr.message);
    return "retry";
  }

  /* La fila se toca por RPC y no con un UPDATE suelto: `scanned_invoices` solo
     deja escribir a admin/responsable, y quien escanea una factura no siempre lo
     es. La función solo puede tocar estas tres columnas y solo en facturas de tu
     empresa, así que abrirla a todo el mundo no abre nada más. */
  const { error: attachErr } = await supabase.rpc("attach_scanned_invoice_file", {
    p_invoice_id: invoiceId,
    p_storage_path: path,
    p_file_mime: job.mime,
    p_file_size: job.blob.size,
  });
  if (attachErr) {
    console.warn("[archiveInvoiceFile] no se pudo enganchar a la factura:", attachErr.message);
    return "retry";
  }

  return "stored";
}

/* Una pasada a la vez: el arranque, el «ha vuelto la red» y el fin de un escaneo
   pueden coincidir, y sin esto subirían el mismo archivo tres veces. */
let flushing: Promise<void> | null = null;

/**
 * Vaciar la cola. Se llama al arrancar la app, al volver la red y al terminar un
 * escaneo. Lo que no salga se queda para la próxima.
 */
export async function flushInvoiceFileOutbox(): Promise<void> {
  if (flushing) return flushing;
  flushing = (async () => {
    try {
      const jobs = await listInvoiceFileJobs();
      for (const job of jobs) {
        await markInvoiceFileAttempt(job.fileHash);
        let outcome: ArchiveOutcome;
        try {
          outcome = await archiveInvoiceFileOnce(job);
        } catch (err) {
          console.warn("[archiveInvoiceFile] intento fallido:", err);
          outcome = "retry";
        }
        if (outcome !== "retry") await dropInvoiceFileJob(job.fileHash);
      }
    } finally {
      flushing = null;
    }
  })();
  return flushing;
}
